import type { RegistryItemPreviewResult } from "@replohq/bedrock-sdk";
import type { PropsWithChildren } from "react";
import type { WorkspaceDefaultProjectType } from "schemas/generated/workspaceDefaultProjectType";

import * as React from "react";

import AuthGradientBackground from "@svg/auth-gradient-background";

import { BedrockClient } from "@replohq/bedrock-sdk";
import { BedrockProvider } from "@replohq/bedrock-sdk/provider";
import { useBedrock } from "@replohq/bedrock-sdk/react";
import {
  getPendingRegistryInstallItemId,
  savePendingRegistryInstallItemId,
} from "@replohq/lattice/components/registry-item-preview/pendingRegistryInstall";
import { RegistryItemPreview } from "@replohq/lattice/components/registry-item-preview/RegistryItemPreview";
import { Skeleton } from "@replohq/lattice/components/skeleton/Skeleton";
import { cn } from "@replohq/lattice/utils/cn";
import { DURATION, EASING } from "@replohq/lattice/utils/motion";
import { skipToken, useQuery } from "@tanstack/react-query";
import {
  Screen,
  ScreenData,
  useNextUrlWithPendingAppInstallationId,
} from "@v2/features/account/common";
import { ProductShowcasePanel } from "@v2/features/account/product-showcase-panel/ProductShowcasePanel";
import { analytics } from "@v2/features/infra/analytics/tracking";
import useCurrentUser from "@v2/hooks/useCurrentUser";
import { fileTemplateRouter } from "@v2/trpc/routes/fileTemplate";
import { getBedrockApiUrl, getCoordinatorApiUrl } from "@v2/utils/env";
import { readFreshMarketingSeedAndPurgeStale } from "@v2/utils/marketing-seed";
import { routes } from "@v2/utils/router";
import { UserRound } from "lucide-react";
import { motion, useReducedMotion } from "motion/react";
import {
  Navigate,
  Outlet,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import useMeasure from "react-use-measure";

import {
  getPersistedSignupProjectType,
  getSignupProjectTypeFromParam,
  hasShopifyInstallIntent,
  isAgentEnableAuthIntent,
  persistSignupProjectType,
  resolveSignupDefaultProjectType,
} from "./signupFunnelUtils";

type PendingRegistryInstallPreviewItem = RegistryItemPreviewResult["item"];
const pendingRegistryInstallPreviewDesktopWidth = 1440;

function PublicRegistryBedrockProvider({ children }: PropsWithChildren) {
  const client = React.useMemo(() => {
    return new BedrockClient({
      appId: "registry-preview",
      bedrockApiUrl: getBedrockApiUrl(),
      agentCoordinatorBaseUrl: getCoordinatorApiUrl(),
      auth: { type: "public" },
    });
  }, []);

  return <BedrockProvider client={client}>{children}</BedrockProvider>;
}

const pendingRegistryInstallTypeLabelByType: Record<
  PendingRegistryInstallPreviewItem["type"],
  string
> = {
  site: "Shared site",
  page: "Shared page",
  section: "Shared section",
  skill: "Shared skill",
  branding: "Shared brand kit",
  plan: "Shared plan",
  product: "Shared product",
  reference: "Shared reference",
};

export function AuthLayout() {
  const { isAuthenticated } = useCurrentUser();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { nextUrl, nextSearchParams } =
    useNextUrlWithPendingAppInstallationId();

  const pageTemplateId = searchParams.get("pageTemplateId") ?? null;
  if (pageTemplateId) {
    sessionStorage.setItem("replo_pending_template_id", pageTemplateId);
  }
  // Share pages route anonymous visitors here through the item owner's
  // affiliate link (cross-origin), so the pending install arrives as a query
  // param instead of sessionStorage.
  const registryItemIdParam = searchParams.get("registryItemId") ?? null;
  if (registryItemIdParam) {
    savePendingRegistryInstallItemId({ itemId: registryItemIdParam });
  }
  const pendingRegistryInstallItemId =
    registryItemIdParam ?? getPendingRegistryInstallItemId();

  // (Yuxin, 2026-07-18): A `next` back to /beta/enable means beta opt-in — lock the funnel to Agent and show welcome copy.
  const isAgentEnableIntent = isAgentEnableAuthIntent({
    pathname: location.pathname,
    searchParams,
  });
  const isSignup = location.pathname === Screen.SIGNUP;
  const authRoute: "signup" | "login" = isSignup ? "signup" : "login";
  const hasTrackedInitiatedRef = React.useRef(false);

  // eslint-disable-next-line replo/no-use-effect -- Legacy effect - may or may not be necessary
  React.useEffect(() => {
    if (pageTemplateId && !hasTrackedInitiatedRef.current) {
      hasTrackedInitiatedRef.current = true;
      analytics.logEvent("sharedPageTemplate.auth.initiated", {
        templateId: pageTemplateId,
        authRoute,
      });
    }
  }, [pageTemplateId, authRoute]);

  const marketingSiteSeedData = readFreshMarketingSeedAndPurgeStale();
  const marketingSitePrompt = marketingSiteSeedData
    ? marketingSiteSeedData.prompt
    : null;
  const marketingSiteImage =
    marketingSiteSeedData?.hasImage && marketingSiteSeedData.jti
      ? `https://marketing-uploads.replocdn.com/marketing-website-prompt-uploads/${marketingSiteSeedData.jti}`
      : "";

  // NOTE (Yuxin, 2026-07-13, GROW-5633): `?type=agent`/`?type=shopify` and the
  // in-UI funnel switch both persist the choice, so a returning visitor without
  // the param defaults back to whichever funnel they last landed on or picked.
  // TODO (Ben, 2026-06-11, REPL-27910): default to agent path at launch
  const [userSelectedPath, setUserSelectedPath] =
    React.useState<WorkspaceDefaultProjectType | null>(null);
  const isShopifyInstallIntent = hasShopifyInstallIntent(searchParams);
  const projectTypeFromParam = getSignupProjectTypeFromParam(
    searchParams.get("type"),
  );
  // Persist the URL-param funnel, but never clobber a switch the visitor made
  // this session — that path is re-rendered with the param still in the URL.
  if (projectTypeFromParam && !userSelectedPath && !isShopifyInstallIntent) {
    persistSignupProjectType(projectTypeFromParam);
  }
  const persistedProjectType = getPersistedSignupProjectType();
  const isFunnelSwitchEnabled = !isAgentEnableIntent && !isShopifyInstallIntent;
  let defaultProjectType: WorkspaceDefaultProjectType;
  if (isAgentEnableIntent) {
    defaultProjectType = "agent";
  } else if (isShopifyInstallIntent) {
    defaultProjectType = "shopify_theme_builder";
  } else {
    defaultProjectType = resolveSignupDefaultProjectType({
      userSelected: userSelectedPath,
      fromParam: projectTypeFromParam,
      persisted: persistedProjectType,
    });
  }
  const setDefaultProjectType = React.useCallback(
    (nextProjectType: WorkspaceDefaultProjectType) => {
      persistSignupProjectType(nextProjectType);
      setUserSelectedPath(nextProjectType);
    },
    [],
  );

  const isPasswordResetRoute = location.pathname.startsWith(
    "/auth/password/reset",
  );
  if (isAuthenticated && !isPasswordResetRoute) {
    // `next=/beta/enable` lands on the picker via the standard next-url redirect below.
    return (
      <Navigate
        to={{
          pathname: nextUrl ?? routes.home.root,
          search: nextSearchParams.toString(),
        }}
        replace
      />
    );
  }

  const renderData = ScreenData[location.pathname ?? ""];

  return (
    <div className="no-scrollbar flex w-full flex-col items-center justify-center gap-0 overflow-x-hidden overflow-y-scroll px-4 md:py-10 lg:h-screen lg:w-screen lg:flex-row lg:gap-0 lg:px-0 lg:py-0">
      <div className="order-2 flex h-full w-full max-w-[400px] flex-col items-center justify-center lg:order-1 lg:w-1/2 lg:max-w-none lg:items-stretch lg:p-0">
        <AuthPageLeftPanel
          pageTemplateId={pageTemplateId}
          pendingRegistryInstallItemId={pendingRegistryInstallItemId}
          isSignup={isSignup}
          hasMarketingSiteSeedData={Boolean(marketingSiteSeedData)}
          marketingSitePrompt={marketingSitePrompt}
          marketingSiteImage={marketingSiteImage}
          defaultProjectType={defaultProjectType}
          onSwitchPath={
            isSignup && isFunnelSwitchEnabled ? setDefaultProjectType : null
          }
        />
      </div>
      <div className="order-1 my-4 flex w-full items-center justify-center lg:order-2 lg:my-0 lg:w-1/2">
        <div className="mx-auto flex w-full flex-col items-center gap-6 sm:h-full sm:max-w-[370px] md:mx-0 md:justify-center">
          <div className="flex w-full flex-col gap-3">
            <Outlet
              context={{
                defaultProjectType,
                setDefaultProjectType,
              }}
            />
            <div className="text-placeholder text-sm">
              {renderData?.footerText}
              <span
                className="text-primary cursor-pointer pl-2"
                onClick={() => {
                  const query = getQueryDefaultsFromLocation(searchParams);
                  // An agent-funnel visitor with an existing account should land
                  // in the /beta/enable flow after login instead of their old
                  // theme-builder dashboard.
                  const shouldCarryAgentEnableNext =
                    isSignup && defaultProjectType === "agent" && !query.next;
                  const footerCtaSearchParams = new URLSearchParams(
                    searchParams,
                  );
                  if (shouldCarryAgentEnableNext) {
                    footerCtaSearchParams.set("next", Screen.BETA_ENABLE);
                  }
                  if (renderData?.footerCtaRedirectTo) {
                    // Note (Sebas, 2023-06-30): This is the place where we set the next
                    // parameter for the login/signup flow to prevent losing the value.
                    navigate(
                      // Note (Noah, 2024-02-14): Preserve query strings in this navigate call, since
                      // we have query params like shopifyShopURL and pendingInstallationId we need to
                      // preserve in order to make the claim flow work if they just came from installing
                      // the shopify integration
                      {
                        pathname: renderData.footerCtaRedirectTo,
                        search: footerCtaSearchParams.toString(),
                      },
                      {
                        state: {
                          next: shouldCarryAgentEnableNext
                            ? Screen.BETA_ENABLE
                            : query.next ?? null,
                        },
                      },
                    );
                  }
                }}
              >
                {renderData?.footerCtaText}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function AuthPageLeftPanel({
  pageTemplateId,
  pendingRegistryInstallItemId,
  isSignup,
  hasMarketingSiteSeedData,
  marketingSitePrompt,
  marketingSiteImage,
  defaultProjectType,
  onSwitchPath,
}: {
  pageTemplateId: string | null;
  pendingRegistryInstallItemId: string | null;
  isSignup: boolean;
  hasMarketingSiteSeedData: boolean;
  marketingSitePrompt: string | null;
  marketingSiteImage: string;
  defaultProjectType: WorkspaceDefaultProjectType;
  onSwitchPath: ((next: WorkspaceDefaultProjectType) => void) | null;
}) {
  if (pageTemplateId) {
    return (
      <TemplatePreview
        pageTemplateId={pageTemplateId}
        isSignup={isSignup}
        defaultProjectType={defaultProjectType}
      />
    );
  }

  if (pendingRegistryInstallItemId) {
    return (
      <PublicRegistryBedrockProvider>
        <PendingRegistryInstallPreviewPanel
          itemId={pendingRegistryInstallItemId}
          isSignup={isSignup}
          defaultProjectType={defaultProjectType}
        />
      </PublicRegistryBedrockProvider>
    );
  }

  if (hasMarketingSiteSeedData && marketingSitePrompt) {
    return (
      <ReploChatPreview
        marketingSitePrompt={marketingSitePrompt}
        marketingSiteImage={marketingSiteImage}
      />
    );
  }

  return (
    <ProductShowcasePanel
      defaultProjectType={defaultProjectType}
      onSwitchPath={onSwitchPath}
    />
  );
}

function PendingRegistryInstallPreviewPanel({
  itemId,
  isSignup,
  defaultProjectType,
}: {
  itemId: string;
  isSignup: boolean;
  defaultProjectType: WorkspaceDefaultProjectType;
}) {
  const { client } = useBedrock();
  const shouldReduceMotion = useReducedMotion();
  const { data, isError, isLoading } = useQuery({
    queryKey: ["pending-registry-install-preview", itemId],
    queryFn: () => client.registry.items.getPreview({ id: itemId }),
    retry: false,
  });
  const item = data?.item ?? null;
  const itemTitle = item?.name ?? "Shared item";
  const itemTypeLabel = item
    ? pendingRegistryInstallTypeLabelByType[item.type]
    : "Shared item";
  const ctaText = isSignup
    ? "Sign up to add this shared item to your workspace."
    : "Log in to add this shared item to your workspace.";

  if (isError || (!isLoading && !item)) {
    return (
      <ProductShowcasePanel
        defaultProjectType={defaultProjectType}
        onSwitchPath={null}
      />
    );
  }

  return (
    <div className="relative hidden h-full w-full overflow-hidden bg-black lg:block">
      <div className="absolute inset-0 flex items-end justify-center overflow-hidden">
        <AuthGradientBackground className="absolute bottom-0 h-auto min-h-full w-full" />
      </div>
      <motion.div
        className="relative z-10 flex h-full flex-col items-center justify-end overflow-hidden"
        initial={
          shouldReduceMotion ? false : { opacity: 0, y: 24, scale: 0.98 }
        }
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{
          duration: shouldReduceMotion ? 0 : DURATION.slow,
          ease: EASING.outQuint,
        }}
      >
        <div className="flex shrink-0 flex-col items-center gap-2 px-12 text-center text-brand-seashell">
          <div className="text-sm font-medium text-brand-seashell/70">
            {itemTypeLabel}
          </div>
          <h2 className="line-clamp-2 font-editorial text-4xl leading-tight">
            {itemTitle}
          </h2>
          <p className="max-w-md text-sm leading-6 text-brand-seashell/70">
            {ctaText}
          </p>
        </div>
        <div className="flex w-full justify-center px-8 pt-10">
          <div className="-mb-20 h-[calc(100vh-220px)] min-h-[420px] w-[min(45vw,900px)] overflow-hidden rounded-t-lg bg-sheet shadow-2xl ring-1 ring-brand-seashell/15">
            <RegistryItemPreview
              item={item}
              isLoading={isLoading}
              isError={false}
              className="h-full w-full"
              htmlViewportWidth={pendingRegistryInstallPreviewDesktopWidth}
              disableHtmlScroll
            />
          </div>
        </div>
      </motion.div>
    </div>
  );
}

function TemplatePreview({
  pageTemplateId,
  isSignup,
  defaultProjectType,
}: {
  pageTemplateId: string;
  isSignup: boolean;
  defaultProjectType: WorkspaceDefaultProjectType;
}) {
  const { data: template, isLoading } = fileTemplateRouter.getPublic.useQuery(
    pageTemplateId ? { id: pageTemplateId } : skipToken,
  );

  const [containerRef, containerDimensions] = useMeasure();

  const desktopWidth = 1440;
  const scale = React.useMemo(() => {
    if (containerDimensions.width > 0) {
      return containerDimensions.width / desktopWidth;
    }
    return 0.5; // Default scale
  }, [containerDimensions.width]);

  const ctaText = isSignup
    ? "Sign up to start building."
    : "Log in to start building.";

  if (isLoading) {
    return (
      <div className="hidden h-full w-full overflow-hidden bg-black lg:flex lg:items-center lg:justify-center">
        <Skeleton className="w-3/4 h-3/4" />
      </div>
    );
  }

  if (!template || !template.staticHtml) {
    return (
      <ProductShowcasePanel
        defaultProjectType={defaultProjectType}
        onSwitchPath={null}
      />
    );
  }

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Archivo:wght@400;500;600;700&display=swap');

        @font-face {
          font-family: 'PP Editorial New';
          src: url('https://assets.replocdn.com/PPEditorialNew-Regular.woff2') format('woff2');
          font-weight: normal;
          font-style: normal;
          font-display: swap;
        }

        @font-face {
          font-family: 'PP Editorial New';
          src: url('https://assets.replocdn.com/PPEditorialNew-Italic.woff2') format('woff2');
          font-weight: normal;
          font-style: italic;
          font-display: swap;
        }

      `}</style>
      <div className="hidden h-full w-full overflow-hidden bg-black relative lg:block">
        <div className="absolute inset-0 flex items-end justify-center overflow-hidden">
          <AuthGradientBackground className="absolute bottom-0 w-full h-auto min-h-full" />
        </div>

        {/* Content + Preview Container */}
        <div className="relative z-10 h-full flex flex-col items-center justify-end pb-0">
          {/* Content */}
          <div className="flex flex-col items-center px-12 pb-12">
            <div className="text-4xl text-white font-editorial text-center mb-2">
              {template.name ?? "Untitled Template"}
            </div>
            <div className="text-base text-white/80 font-light text-center">
              {ctaText}
            </div>
          </div>

          {/* Template preview - sticks up from bottom and extends beyond */}
          <div className="w-full flex justify-center px-8 pt-12">
            <div
              ref={containerRef}
              className="overflow-hidden rounded-t-lg shadow-2xl -mb-20 w-[min(45vw,900px)] h-[calc(100vh-220px)]"
            >
              <div
                className="origin-top-left overflow-y-auto rounded-t-lg bg-white w-[1440px] no-scrollbar"
                style={{
                  height: `${100 / scale}%`,
                  transform: `scale(${scale})`,
                }}
              >
                <iframe
                  srcDoc={template.staticHtml}
                  className="w-full h-full bg-white"
                  sandbox="allow-scripts allow-same-origin"
                  title="Template Preview"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

function ReploChatPreview({
  marketingSitePrompt,
  marketingSiteImage,
}: {
  marketingSitePrompt: string;
  marketingSiteImage: string;
}) {
  return (
    <div className="px-25 py-30 hidden h-full w-full overflow-hidden bg-black bg-[url('/images/onboarding/gradient-background.png')] bg-cover bg-center lg:block">
      <div
        className={cn(
          "flex flex-col items-center gap-6",
          marketingSiteImage ? "pt-20" : "pt-30",
        )}
      >
        <div className="w-full max-w-2xl">
          <div className="flex flex-row gap-4 w-full items-end">
            <div className="w-10 h-10 bg-[url('/images/onboarding/replo-logo-green-background.svg')] bg-cover bg-center flex-shrink-0" />
            <div className="flex flex-col gap-3 justify-end flex-1 min-w-0">
              <div className="text-brand-seashell text-base font-light">
                Replo AI
              </div>
              <div className="border border-brand-seashell/20 bg-[#1E1E1EE5] text-brand-seashell text-base font-light rounded-lg p-2 w-full">
                What can I help you build?
              </div>
            </div>
            <div className="w-10 h-10 flex-shrink-0"></div>
          </div>
        </div>
        <div className="w-full max-w-2xl">
          <div className="flex flex-row gap-4 w-full items-end">
            <div className="w-10 h-10 flex-shrink-0"></div>
            <div className="flex flex-col gap-3 justify-end flex-1 min-w-0">
              <div className="flex items-end justify-between">
                <div className="text-brand-seashell text-base font-light">
                  You
                </div>
                {marketingSiteImage && (
                  <div className="w-20 h-20">
                    <img
                      src={marketingSiteImage}
                      alt="Marketing site reference"
                      className="w-full h-full object-cover rounded-lg border border-brand-seashell/20"
                    />
                  </div>
                )}
              </div>
              <div className="border border-brand-seashell/20 bg-white text-black text-base font-light rounded-lg p-2 w-full">
                {marketingSitePrompt}
              </div>
            </div>
            <div className="w-10 h-10 bg-[#1E1E1EE5] flex items-center justify-center flex-shrink-0 border border-brand-seashell/20 rounded-lg">
              <UserRound className="w-5 h-5 text-brand-seashell" />
            </div>
          </div>
        </div>
        <div className="w-full max-w-2xl">
          <div className="flex flex-row gap-4 w-full items-end">
            <div className="w-10 h-10 bg-[url('/images/onboarding/replo-logo-green-background.svg')] bg-cover bg-center flex-shrink-0" />
            <div className="flex flex-col gap-3 justify-end flex-1 min-w-0">
              <div className="text-brand-seashell text-base font-light">
                Replo AI
              </div>
              <div className="border border-brand-seashell/20 bg-[#1E1E1EE5] text-brand-seashell text-base font-light rounded-lg p-2 w-full">
                Great idea! Please sign up to start building.
              </div>
            </div>
            <div className="w-10 h-10 flex-shrink-0"></div>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * We persist these between SIGNUP/LOGIN routes so that when someone installs the app
 * but already has an account, we can link the shopify URL to the correct account.
 */
function getQueryDefaultsFromLocation(searchParams: URLSearchParams) {
  const query: Record<string, string> = {};
  for (const key of [
    "email",
    "first_name",
    "last_name",
    "shopify_shop_url",
    "nonce",
    "next",
  ]) {
    const value = searchParams.get(key);
    if (value) {
      query[key] = value;
    }
  }
  return query;
}
